import { PROMO_TIMEZONE, SALE_SHORT } from '../data/promotion'

/**
 * Calendar-week promotion logic. The deadline is always the upcoming Sunday at
 * 11:59:59 PM America/New_York, computed from the real clock. It never depends
 * on page load, sessions, cookies or the visitor's own timezone, and it handles
 * daylight saving time through Intl instead of a fixed UTC offset.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface EasternParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  weekday: number
}

const formatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PROMO_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  weekday: 'short',
  hourCycle: 'h23',
})

export function getCurrentEasternTime(at: Date = new Date()): EasternParts {
  const parts = formatter.formatToParts(at)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0'
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: WEEKDAYS.indexOf(get('weekday')),
  }
}

/** Offset (ms) between Eastern wall-clock time and UTC at a given instant. */
function easternOffset(at: Date): number {
  const p = getCurrentEasternTime(at)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(at.getTime() / 1000) * 1000
}

/** Converts an Eastern wall-clock time to a real instant, DST-safe. */
function easternWallTimeToDate(year: number, month: number, day: number, h: number, m: number, s: number): Date {
  const guess = Date.UTC(year, month - 1, day, h, m, s)
  let instant = guess - easternOffset(new Date(guess))
  // Second pass settles the offset on DST transition days.
  instant = guess - easternOffset(new Date(instant))
  return new Date(instant)
}

/** The upcoming Sunday at 11:59:59 PM Eastern. On Sunday, that is tonight. */
export function getNextSundayDeadline(now: Date = new Date()): Date {
  const et = getCurrentEasternTime(now)
  const daysUntilSunday = (7 - et.weekday) % 7
  const target = new Date(Date.UTC(et.year, et.month - 1, et.day + daysUntilSunday))
  return easternWallTimeToDate(
    target.getUTCFullYear(),
    target.getUTCMonth() + 1,
    target.getUTCDate(),
    23,
    59,
    59,
  )
}

export interface WeeklyPromoState {
  days: number
  hours: number
  minutes: number
  seconds: number
  /** Short urgency line, adapted to the Eastern weekday. */
  headline: string
  /** Optional second line (Friday only). */
  subline?: string
}

export function calculateRemainingTime(now: Date = new Date()): WeeklyPromoState {
  const deadline = getNextSundayDeadline(now)
  const total = Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / 1000))
  const weekday = getCurrentEasternTime(now).weekday

  let headline = `This week's ${SALE_SHORT} price ends Sunday`
  let subline: string | undefined
  if (weekday === 5) subline = 'Only 2 days left'
  if (weekday === 6) headline = `This week's ${SALE_SHORT} price ends tomorrow`
  if (weekday === 0) headline = `This week's ${SALE_SHORT} price ends tonight`

  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
    headline,
    subline,
  }
}
